import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Stepper } from "@/components/ui/stepper";
import { toast } from "sonner";
import { useAuth } from "@/hooks/auth";
import { Mask } from "@/utils/mask";
import {
  ArrowLeft,
  Users,
  Search,
  User,
  ChevronRight,
  Upload,
  Image,
  Trash2,
  Camera,
} from "lucide-react";
import { CameraModal } from "@/components/CameraModal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getSelectSellers } from "@/Api/sellers";
import { getSelectAllClientsAndLeads } from "@/Api/clients";
import { getPaymentForms } from "@/Api/sales";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { getCircuitRoutesData } from "@/Api/circuit_routes";
import {
  createPreSale,
  getUnityProductsForPreSale,
  getAllUnityProductsForPreSale,
  CreatePreSaleData,
} from "@/Api/preSales";
import { SALE_TYPE_ID, PAYMENT_FORMS } from "@/utils/constants";

// Importar os mesmos componentes da venda
import { ProductSelection } from "../Sales/components/ProductSelection";
import { ClientSelection } from "../Sales/components/ClientSelection";
import { PaymentDeliverySelection } from "../Sales/components/PaymentDeliverySelection";
import { OrderSummary } from "../Sales/components/OrderSummary";
import { SalesProductProps, PaymentFormItem } from "../Sales/Sales";
import { PreSalePixDialog } from "./components/PreSalePixDialog";

interface SelectedClientInfo {
  id: string;
  name: string;
  uuid?: string;
  discount?: number;
  discount_type?: {
    id: number;
    name: string;
  };
  addresses?: Array<{
    id: number;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    zip_code: string;
    pivot: {
      client_id: number;
      address_id: number;
      is_main: number;
      reference: string | null;
      created_at: string;
      updated_at: string;
    };
  }>;
}

interface PreSaleCreateProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const PreSaleCreate = ({ onCancel, onSuccess }: PreSaleCreateProps) => {
  const user = useAuth();
  const isAdmin = user?.user?.user_type_id === 2;
  const isManager = user?.user?.user_type_id === 4;
  const userUnityIds = user?.user?.unity?.map((u: any) => u.id) || [];

  const steps = ["Produtos", "Cliente", "Entrega e Pagamento", "Confirmação"];
  // Admin e Gerente começam na seleção de vendedor (step -1)
  const [currentStep, setCurrentStep] = useState(isAdmin || isManager ? -1 : 0);
  const [_isLoading, setIsLoading] = useState(false);

  // Data states
  const [sellers, setSellers] = useState<any[]>([]);
  const [products, setProducts] = useState<SalesProductProps[]>([]);
  const [clients, setClients] = useState<any>({
    current_page: 1,
    data: [],
    first_page_url: "",
    from: 1,
    last_page: 1,
    last_page_url: "",
    next_page_url: null,
    path: "",
    per_page: 15,
    prev_page_url: null,
    to: 1,
    total: 0,
  });
  const [paymentForms, setPaymentForms] = useState<any[]>([]);
  const [saleTypes, setSaleTypes] = useState<any[]>([]);
  const [routes, setRoutes] = useState<Array<{ id: number; title: string }>>([]);

  // Selection states
  const [selectedSeller, setSelectedSeller] = useState<{
    id: string;
    name: string;
    uuid?: string;
    unity_id?: string;
  }>({} as { id: string; name: string; uuid?: string; unity_id?: string });
  const [selectedClient, setSelectedClient] = useState<SelectedClientInfo | null>(null);
  const [isLead, setIsLead] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<
    Array<{ id: number; quantity: number }>
  >([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<number>(0);
  const [selectedPaymentForms, setSelectedPaymentForms] = useState<PaymentFormItem[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<string>("");
  const [address, setAddress] = useState<any>({
    addressName: "",
    street: "",
    number: "",
    complement: "",
    reference: "",
    neighborhood: "",
    city: "",
    state: "",
    zip_code: "",
    latitude: "",
    longitude: "",
  });

  // Search states
  const [sellerSearch, setSellerSearch] = useState("");

  // File upload states
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  // Total calculation
  const [totalSaleAmount, setTotalSaleAmount] = useState<number>(0);
  const [discountAmount, setDiscountAmount] = useState<number>(0);

  // PIX modal states
  const [showPixModal, setShowPixModal] = useState(false);
  const [createdPaymentAdvances, setCreatedPaymentAdvances] = useState<
    Array<{
      id: number;
      uuid: string;
      amount: number;
      pix_qrcode: string;
      pix_qrcode_image?: string;
      pix_location?: string;
      pix_txid?: string;
      status: string;
    }>
  >([]);

  const DISCOUNT_TYPE = {
    IN_TARIFF_VALUE: 1,
    IN_SALE_VALUE: 2,
  } as const;

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        // Para gerente, filtrar vendedores apenas da unidade dele
        const sellerParams: { is_active: boolean; unity_id?: string } = { is_active: true };
        if (isManager && userUnityIds.length > 0) {
          sellerParams.unity_id = String(userUnityIds[0]);
        }

        const [sellersRes, paymentRes, saleTypesRes, routesRes] = await Promise.all([
          getSelectSellers(sellerParams),
          getPaymentForms(),
          getSelectSaleTypes(),
          getCircuitRoutesData({
            start_date: Mask.getLocalDate(),
            end_date: Mask.getLocalDate(),
          }),
        ]);

        if (sellersRes.ok) setSellers(sellersRes.data?.response || []);
        if (paymentRes.ok) setPaymentForms(paymentRes.data?.response || []);
        if (saleTypesRes.ok) setSaleTypes(saleTypesRes.data?.response || []);
        if (routesRes.ok) setRoutes(routesRes.data?.response || []);

        // Se for vendedor, define o seller automaticamente
        // Admin e gerente precisam selecionar o vendedor manualmente
        if (!isAdmin && !isManager && user?.user?.seller) {
          console.log("Setting seller from user:", user.user.seller);
          console.log("Unity ID:", user.user.seller.unity_id);
          setSelectedSeller({
            id: String(user.user.seller.id),
            name: user.user.name,
            unity_id: String(user.user.seller.unity_id),
          });
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Erro ao carregar dados");
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Load products when unity/seller is selected
  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        let response;
        if (isAdmin && !selectedSeller?.unity_id) {
          // Admin sem vendedor selecionado - busca todos os produtos
          response = await getAllUnityProductsForPreSale();
        } else if (selectedSeller?.unity_id) {
          // Com vendedor selecionado - busca produtos da unidade específica
          response = await getUnityProductsForPreSale(Number(selectedSeller.unity_id));
        } else {
          // Sem vendedor e não é admin - não carrega nada
          setIsLoading(false);
          return;
        }

        if (response.ok) {
          const rawProducts = response.data?.response || [];
          console.log("Raw products from API:", rawProducts);
          // Adaptar os dados para o formato esperado pelo ProductSelection
          const adaptedProducts = rawProducts.map((product: any) => ({
            ...product,
            // Mapear unity_balance para cp_seller_balance para compatibilidade
            cp_seller_balance: {
              id: product.id,
              seller_balance: product.unity_balance || 0,
            },
            // Adicionar contest com id para passar no filtro do ProductSelection
            contest: product.contest || {
              id: product.contest_id,
              name: product.contest_product_classification?.contest?.name || "",
              uuid: product.contest_product_classification?.contest?.uuid || "",
              validation_date: product.contest_product_classification?.contest?.validation_date || "",
            },
            // Adicionar seller_received_without_distribution para não usar estoque adicional
            unity: {
              ...product.unity,
              seller_received_without_distribution: false,
            },
          }));
          console.log("Adapted products:", adaptedProducts);
          setProducts(adaptedProducts);
        }
      } catch (error) {
        console.error("Error loading products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Carregar produtos quando:
    // 1. É admin no step -1 (sem vendedor selecionado ainda)
    // 2. Ou quando selectedSeller está definido (step 0 ou admin com vendedor)
    if ((isAdmin || isManager) && currentStep === -1) {
      // Admin/Gerente ainda não selecionou vendedor, não carrega produtos
      return;
    }

    if (currentStep >= 0 && (selectedSeller?.unity_id || isAdmin)) {
      loadProducts();
    }
  }, [selectedSeller?.unity_id, currentStep, isAdmin]);

  // Calculate totals
  useEffect(() => {
    let total = 0;
    let discount = 0;

    selectedProducts.forEach((selectedProduct) => {
      if (selectedProduct.quantity > 0) {
        const product = products.find((p) => p.id === selectedProduct.id);
        if (product) {
          let price =
            product.contest_product_classification.product_classification?.sale_price || 0;

          if (
            selectedClient?.discount_type?.id === DISCOUNT_TYPE.IN_TARIFF_VALUE &&
            selectedClient?.discount &&
            selectedClient.discount > 0
          ) {
            const discountPerUnit = price * (selectedClient.discount / 100);
            discount += discountPerUnit * selectedProduct.quantity;
            price -= discountPerUnit;
          }

          total += price * selectedProduct.quantity;
        }
      }
    });

    if (
      selectedClient?.discount_type?.id === DISCOUNT_TYPE.IN_SALE_VALUE &&
      selectedClient?.discount &&
      selectedClient.discount > 0
    ) {
      discount = total * (selectedClient.discount / 100);
      total = Math.max(0, total - total * (selectedClient.discount / 100));
    }

    const roundedDiscount = Math.round(discount * 100) / 100;
    const roundedTotal = Math.round(total * 100) / 100;

    setDiscountAmount(roundedDiscount);
    setTotalSaleAmount(roundedTotal);
  }, [selectedProducts, products, selectedClient]);

  const handleSellerSelect = (seller: any) => {
    setSelectedSeller({
      id: String(seller.id),
      name: seller.user?.name,
      unity_id: String(seller.unity_id),
    });
    setIsLead(false);
    setSellerSearch("");
    setCurrentStep(0);
  };

  const handleProductSelection = (productId: number, quantity: number) => {
    setSelectedProducts((prev) => {
      const existing = prev.find((p) => p.id === productId);
      if (existing) {
        return prev.map((p) => (p.id === productId ? { ...p, quantity } : p));
      }
      return [...prev, { id: productId, quantity }];
    });
  };

  const handlePaymentFormsChange = (forms: PaymentFormItem[]) => {
    setSelectedPaymentForms(forms);
  };

  // Fotos do Bolão: aceita só imagens e PDF de até 10MB (seleção, câmera e arrastar-e-soltar)
  const addPreSaleFiles = (files: File[]) => {
    const MAX_SIZE = 10 * 1024 * 1024;
    const valid = files.filter((file) => {
      const isAccepted = file.type.startsWith("image/") || file.type === "application/pdf";
      if (!isAccepted) {
        toast.error(`${file.name}: formato não aceito (use JPG, PNG, WEBP ou PDF).`);
        return false;
      }
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name}: o arquivo passa de 10MB.`);
        return false;
      }
      return true;
    });
    if (valid.length > 0) {
      setSelectedFiles((prev) => [...prev, ...valid]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addPreSaleFiles(Array.from(e.target.files || []));
    // Permite selecionar o mesmo arquivo de novo depois de removê-lo
    e.target.value = "";
  };

  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const handleFilesDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingFiles) setIsDraggingFiles(true);
  };

  const handleFilesDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Ignora quando o mouse só passa para um elemento filho da área
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingFiles(false);
  };

  const handleFilesDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
    addPreSaleFiles(Array.from(e.dataTransfer.files || []));
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCameraCapture = (file: File) => {
    setSelectedFiles((prev) => [...prev, file]);
    toast.success("Foto capturada com sucesso!");
  };

  const updateClientsList = () => {
    const fetchClientsAndLeads = async () => {
      try {
        const response = await getSelectAllClientsAndLeads({
          seller_id: selectedSeller?.id || user?.user?.seller?.id || "",
        });
        if (response && response.data) {
          const data = response.data;

          if (data.status && data.response) {
            const clientsData = data.response.clients || [];
            const leadsData = data.response.leads || [];

            if (selectedClient) {
              const updatedClientInfo =
                clientsData.find((c: any) => String(c.id) === selectedClient.id) ||
                leadsData.find((l: any) => String(l.id) === selectedClient.id);

              if (updatedClientInfo) {
                setSelectedClient({
                  id: String(updatedClientInfo.id),
                  name: updatedClientInfo.name,
                  uuid: updatedClientInfo.uuid || "",
                  ...(updatedClientInfo || {}),
                });
              }
            }

            setClients({
              current_page: 1,
              data: [
                ...clientsData,
                ...(leadsData || []).map((lead: any) => ({
                  ...lead,
                  addresses: [],
                  isLead: true,
                })),
              ],
              first_page_url: "",
              from: 1,
              last_page: 1,
              last_page_url: "",
              next_page_url: null,
              path: "",
              per_page: clientsData.length,
              prev_page_url: null,
              to: clientsData.length,
              total: clientsData.length,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching clients and leads:", error);
      }
    };

    fetchClientsAndLeads();
  };

  // Clear incompatible payment forms when delivery method changes
  useEffect(() => {
    if (
      selectedDeliveryMethod === SALE_TYPE_ID.ENDOSSO ||
      selectedDeliveryMethod === SALE_TYPE_ID.SEDEX
    ) {
      if (
        selectedPaymentForms.some(
          (form) =>
            form.payment_form_id !== PAYMENT_FORMS.PIX &&
            form.payment_form_id !== PAYMENT_FORMS.PIX_BTG &&
            form.payment_form_id !== PAYMENT_FORMS.REINVESTMENT &&
            form.payment_form_id !== PAYMENT_FORMS.VALE &&
            form.payment_form_id !== PAYMENT_FORMS.PIX_MAQUINETA &&
            form.payment_form_id !== PAYMENT_FORMS.DEPOSIT
        )
      ) {
        setSelectedPaymentForms([]);
      }
    }
  }, [selectedDeliveryMethod]);

  const handleSubmit = async () => {
    const validProducts = selectedProducts.filter((product) => product.quantity > 0);

    if (!selectedSeller?.id) {
      toast.error("Selecione um vendedor");
      return;
    }

    if (validProducts.length === 0) {
      toast.error("Selecione pelo menos um produto");
      return;
    }

    if (!selectedClient) {
      toast.error("Selecione um cliente");
      return;
    }

    if (!selectedDeliveryMethod) {
      toast.error("Selecione o tipo de venda");
      return;
    }

    if (selectedPaymentForms.length === 0) {
      toast.error("Adicione pelo menos uma forma de pagamento");
      return;
    }

    const totalPaid = selectedPaymentForms.reduce((sum, form) => sum + form.value, 0);
    if (Math.abs(totalPaid - totalSaleAmount) > 0.01) {
      toast.error("O valor total pago deve ser igual ao valor da venda");
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error("É necessário enviar pelo menos uma foto do bolão");
      return;
    }

    if (
      (selectedDeliveryMethod === SALE_TYPE_ID.ENDOSSO ||
        selectedDeliveryMethod === SALE_TYPE_ID.SEDEX) &&
      selectedPaymentForms.some(
        (form) =>
          form.payment_form_id !== PAYMENT_FORMS.PIX &&
          form.payment_form_id !== PAYMENT_FORMS.PIX_BTG &&
          form.payment_form_id !== PAYMENT_FORMS.REINVESTMENT &&
          form.payment_form_id !== PAYMENT_FORMS.VALE &&
          form.payment_form_id !== PAYMENT_FORMS.PIX_MAQUINETA &&
          form.payment_form_id !== PAYMENT_FORMS.DEPOSIT &&
          form.payment_form_id !== PAYMENT_FORMS.PIX_CNPJ &&
          form.payment_form_id !== PAYMENT_FORMS.MONEY &&
          form.payment_form_id !== PAYMENT_FORMS.MONEY_BLEED &&
          form.payment_form_id !== PAYMENT_FORMS.ADVANCE_PAYMENT
      )
    ) {
      toast.error(
        "Este tipo de venda só permite pagamento via PIX, PIX Maquineta, Reinvestimento, Vale, Dinheiro, Pix Gerado ou Depósito"
      );
      return;
    }

    try {
      const data: CreatePreSaleData = {
        unity_id: Number(selectedSeller?.unity_id || user?.user?.seller?.unity_id),
        seller_id: Number(selectedSeller.id),
        client_id: isLead ? null : selectedClient ? Number(selectedClient.id) : null,
        lead_id: isLead ? (selectedClient ? Number(selectedClient.id) : null) : null,
        sale_type_id: selectedDeliveryMethod,
        payment_forms: selectedPaymentForms.map((form) => ({
          payment_form_id: form.payment_form_id,
          value: Number(Number(form.value).toFixed(2)),
          ...(form.payment_advance_id && { payment_advance_id: form.payment_advance_id }),
        })),
        address: {
          addressName: address?.addressName || "",
          street: address?.street || "",
          number: address?.number || "",
          complement: address?.complement || "",
          neighborhood: address?.neighborhood || "",
          city: address?.city || "",
          state: address?.state || "",
          zip_code: address?.zip_code || "",
          latitude: address?.latitude ? Number(address?.latitude) : "undefined",
          longitude: address?.longitude ? Number(address?.longitude) : "undefined",
        },
        circuit_route_id: selectedRoute || undefined,
        contest_product_classifications: validProducts.map((p) => {
          const product = products.find((item) => item.id === p.id);
          return {
            id: product?.cp_classification_id || 0,
            quantity: p.quantity,
          };
        }),
      };

      const response = await createPreSale(data, selectedFiles);

      if (response.ok) {
        const responseData = response.data?.response;

        // Verifica se há PIX criados para mostrar o modal
        if (responseData?.created_payment_advances && responseData.created_payment_advances.length > 0) {
          setCreatedPaymentAdvances(responseData.created_payment_advances);
          setShowPixModal(true);
          toast.success("Pre-venda criada com sucesso! Realize o pagamento do PIX.");
        } else {
          toast.success("Pre-venda criada com sucesso! Aguardando aprovacao.");
          onSuccess();
        }
      } else {
        toast.error(response.data?.message || "Erro ao criar pre-venda");
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao criar pre-venda");
    }
  };

  const handlePixModalClose = () => {
    setShowPixModal(false);
    setCreatedPaymentAdvances([]);
    onSuccess();
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 0:
        return selectedProducts.some((product) => product.quantity > 0);
      case 1:
        return !!selectedClient;
      case 2:
        const isSpecialType =
          selectedDeliveryMethod === SALE_TYPE_ID.ENDOSSO ||
          selectedDeliveryMethod === SALE_TYPE_ID.SEDEX;

        if (isSpecialType) {
          return !!selectedDeliveryMethod;
        }

        const totalPaid = selectedPaymentForms.reduce((sum, form) => sum + form.value, 0);
        return (
          !!selectedDeliveryMethod &&
          selectedPaymentForms.length > 0 &&
          Math.abs(totalPaid - totalSaleAmount) < 0.01
        );
      default:
        return true;
    }
  };

  const filteredSellers = sellers.filter((s) =>
    s.user?.name?.toLowerCase().includes(sellerSearch.toLowerCase())
  );

  const renderStep = () => {
    switch (currentStep) {
      case -1:
        return (
          <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="text-center space-y-2">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold">Selecione um Vendedor</h2>
              <p className="text-gray-500">
                Escolha o vendedor para prosseguir com a pré-venda
              </p>
            </div>

            <Card className="border-2">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  Buscar Vendedor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Digite o nome do vendedor..."
                    value={sellerSearch}
                    onChange={(e) => setSellerSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <ScrollArea className="h-[400px]">
                  {filteredSellers.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {filteredSellers.map((seller) => (
                        <Card
                          key={seller.id}
                          className="cursor-pointer transition-all border-2 hover:border-blue-400 hover:shadow-md hover:bg-blue-50"
                          onClick={() => handleSellerSelect(seller)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                                {seller?.user?.name?.charAt(0)?.toUpperCase() || "V"}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">
                                  {seller?.user?.name}
                                </p>
                                {seller?.unity?.name && (
                                  <p className="text-sm text-gray-500">
                                    {seller.unity.name}
                                  </p>
                                )}
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <User className="w-12 h-12 mb-3 text-gray-300" />
                      <p className="font-medium">Nenhum vendedor encontrado</p>
                      {sellerSearch && (
                        <p className="text-sm">Tente buscar por outro nome</p>
                      )}
                    </div>
                  )}
                </ScrollArea>

                <div className="pt-2 border-t text-center text-sm text-gray-500">
                  {filteredSellers.length} vendedor{filteredSellers.length !== 1 ? "es" : ""}{" "}
                  disponível{filteredSellers.length !== 1 ? "is" : ""}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 0:
        return (
          <ProductSelection
            products={products}
            selectedProducts={selectedProducts}
            onProductSelect={handleProductSelection}
            hideSellerBalance
          />
        );

      case 1:
        return (
          <ClientSelection
            clients={clients}
            sellerId={selectedSeller?.id || user?.user?.seller?.id || ""}
            selectedClient={selectedClient?.id || ""}
            address={address}
            onAddressChange={setAddress}
            onClientSelect={(clientId, clientIsLead, name, uuid, discount, discountType) => {
              setSelectedClient({
                id: clientId,
                name: name || "Cliente não selecionado",
                uuid: uuid || "",
                discount: discount,
                discount_type: discountType,
              });
              setIsLead(clientIsLead || false);
            }}
            onNewClient={() => updateClientsList()}
          />
        );

      case 2:
        return (
          <PaymentDeliverySelection
            deliveryMethods={saleTypes}
            paymentForms={paymentForms}
            selectedDeliveryMethod={selectedDeliveryMethod}
            routes={routes}
            selectedRoute={selectedRoute}
            onRouteSelect={setSelectedRoute}
            onDeliveryMethodSelect={(id) => {
              setSelectedDeliveryMethod(id);
            }}
            onAddressChange={setAddress}
            address={address}
            selectedPaymentForms={selectedPaymentForms}
            onPaymentFormsChange={handlePaymentFormsChange}
            totalSaleAmount={totalSaleAmount}
            discountAmount={discountAmount}
            selectedClientUuid={selectedClient?.uuid || ""}
            selectedClientId={selectedClient?.id ? Number(selectedClient.id) : undefined}
            selectedSellerId={
              selectedSeller?.id ? Number(selectedSeller.id) : user?.user?.seller?.id
            }
            selectedClientName={selectedClient?.name}
            selectedProducts={selectedProducts}
            products={products}
          />
        );

      case 3:
        const clientAddressInfo = clients.data.find(
          (c: any) => c.id === Number(selectedClient?.id)
        );

        return (
          <div className="space-y-6">
            {/* Upload de fotos do bolão */}
            {/* <Card className="border-2 border-orange-200 bg-orange-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-600" />
                  Fotos do Bolão (Obrigatório)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <div
                    className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                      isDraggingFiles
                        ? "border-orange-500 bg-orange-50"
                        : "border-orange-300 hover:border-orange-500 bg-white"
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleFilesDragOver}
                    onDragEnter={handleFilesDragOver}
                    onDragLeave={handleFilesDragLeave}
                    onDrop={handleFilesDrop}
                  >
                    <Upload className="h-8 w-8 mx-auto text-orange-400 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {isDraggingFiles ? "Solte os arquivos aqui" : "Clique para selecionar ou arraste as fotos"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG, PNG, WEBP ou PDF (máx. 10MB cada)
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsCameraOpen(true)}
                    className="flex flex-col items-center justify-center h-[120px] w-24 border-2 border-dashed border-orange-300 rounded-lg cursor-pointer bg-white hover:border-orange-500 transition-colors"
                    title="Tirar foto"
                  >
                    <Camera className="w-8 h-8 mb-1 text-orange-400" />
                    <p className="text-xs text-muted-foreground font-semibold">
                      Tirar foto
                    </p>
                  </button>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Arquivos selecionados:</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-white rounded-lg p-2 border"
                        >
                          <div className="flex items-center gap-2">
                            <Image className="h-4 w-4 text-gray-400" />
                            <span className="text-sm truncate max-w-[300px]">{file.name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveFile(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedFiles.length === 0 && (
                  <p className="text-sm text-orange-600 font-medium">
                    * É necessário enviar pelo menos uma foto do bolão para criar a pré-venda
                  </p>
                )}
              </CardContent>
            </Card> */}

            <OrderSummary
              selectedProducts={selectedProducts}
              products={products}
              client={{
                id: Number(selectedClient?.id || 0),
                name: selectedClient?.name || "Cliente não selecionado",
                uuid: clientAddressInfo?.uuid || "",
                phone: clientAddressInfo?.phone || "",
                email: clientAddressInfo?.email || "",
                document: clientAddressInfo?.document,
                addresses: clientAddressInfo?.addresses || [],
                isLead: isLead,
                discount: selectedClient?.discount,
                discount_type: selectedClient?.discount_type,
                discount_type_id: selectedClient?.discount_type?.id,
              }}
              deliveryMethod={
                saleTypes.find((d) => d.id === selectedDeliveryMethod)?.name || ""
              }
              paymentForms={selectedPaymentForms}
              paymentFormsData={paymentForms}
              address={address}
              onConfirm={handleSubmit}
              totalAmount={totalSaleAmount}
              discountAmount={discountAmount}
            >
              <Card className="border-2 border-orange-200 bg-orange-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Upload className="w-5 h-5 text-orange-600" />
                    Fotos do Bolão (Obrigatório)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        isDraggingFiles
                          ? "border-orange-500 bg-orange-50"
                          : "border-orange-300 hover:border-orange-500 bg-white"
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={handleFilesDragOver}
                      onDragEnter={handleFilesDragOver}
                      onDragLeave={handleFilesDragLeave}
                      onDrop={handleFilesDrop}
                    >
                      <Upload className="h-8 w-8 mx-auto text-orange-400 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {isDraggingFiles ? "Solte os arquivos aqui" : "Clique para selecionar ou arraste as fotos"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        JPG, PNG, WEBP ou PDF (máx. 10MB cada)
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,.pdf"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsCameraOpen(true)}
                      className="flex flex-col items-center justify-center h-[120px] w-24 border-2 border-dashed border-orange-300 rounded-lg cursor-pointer bg-white hover:border-orange-500 transition-colors"
                      title="Tirar foto"
                    >
                      <Camera className="w-8 h-8 mb-1 text-orange-400" />
                      <p className="text-xs text-muted-foreground font-semibold">
                        Tirar foto
                      </p>
                    </button>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Arquivos selecionados:</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-white rounded-lg p-2 border"
                          >
                            <div className="flex items-center gap-2">
                              <Image className="h-4 w-4 text-gray-400" />
                              <span className="text-sm truncate max-w-[300px]">{file.name}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-500 hover:text-red-700"
                              onClick={() => handleRemoveFile(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedFiles.length === 0 && (
                    <p className="text-sm text-orange-600 font-medium">
                      * É necessário enviar pelo menos uma foto do bolão para criar a pré-venda
                    </p>
                  )}
                </CardContent>
              </Card>
            </OrderSummary>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Modal do PIX */}
      <PreSalePixDialog
        isOpen={showPixModal}
        onClose={handlePixModalClose}
        paymentAdvances={createdPaymentAdvances}
      />

      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl font-semibold">Nova Pre-Venda</h1>
      </div>

      {/* Aviso de pré-venda - aparece no topo */}
      <Card className="bg-orange-50 border-orange-200">
        <CardContent className="p-4">
          <p className="text-sm text-orange-800">
            Esta pré-venda será enviada para aprovação. Após aprovada, a distribuição
            será criada e o estoque será movido para o vendedor.
          </p>
        </CardContent>
      </Card>

      <Stepper steps={steps} currentStep={currentStep} />

      <div className="min-h-[500px]">{renderStep()}</div>

      <div className="flex justify-between pt-4 border-t">
        {(currentStep > 0 || (currentStep === 0 && (isAdmin || isManager))) && (
          <Button variant="outline" onClick={() => setCurrentStep((prev) => prev - 1)}>
            Voltar
          </Button>
        )}
        {currentStep >= 0 && currentStep < steps.length - 1 && (
          <Button
            onClick={() => setCurrentStep((prev) => prev + 1)}
            disabled={!canProceedToNextStep()}
            className="ml-auto"
          >
            Próximo
          </Button>
        )}
      </div>

      <CameraModal
        isOpen={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
};
